import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, UserPlus, LogIn, Store } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { trackEvent } from "@/lib/analytics";

const STORAGE_KEY = "raneen_welcome_last_shown";

const copy = {
  ar: {
    title: "أهلاً بيك في موقع البائعين الجديد",
    subtitle: "اختر الخطوة اللي تناسبك علشان تبدأ رحلتك مع رنين",
    newTitle: "بائع جديد؟",
    newDesc: "لو لسه بتبدأ، اطلع على تفاصيل برنامج البائعين بتاعنا أو سجل دلوقتي.",
    newCta: "اعرف برنامج البائعين",
    newAlt: "سجل دلوقتي",
    oldTitle: "بائع حالي؟",
    oldDesc: "لو عندك حساب بالفعل، تقدر تسجل دخولك أو تستكشف برنامج البائعين الجديد.",
    oldCta: "تسجيل الدخول",
    oldAlt: "البرنامج الجديد",
    dismiss: "تمام، شكراً",
  },
  en: {
    title: "Welcome to the new seller website",
    subtitle: "Pick the path that fits you to get started with raneen",
    newTitle: "New seller?",
    newDesc: "If you're new here, check out our seller program details or register now.",
    newCta: "Explore seller program",
    newAlt: "Register now",
    oldTitle: "Existing seller?",
    oldDesc: "If you already sell with us, log in or explore our new seller program.",
    oldCta: "Log in",
    oldAlt: "New seller program",
    dismiss: "Got it, thanks",
  },
};

const WelcomeDialog = () => {
  const { lang, localePath } = useLanguage();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Tracks the action label used for the in-flight close so the outer
  // onOpenChange handler doesn't double-count clicks routed through buttons.
  const [pendingCloseAction, setPendingCloseAction] = useState<string | null>(null);
  // Guards against rapid duplicate clicks (double-tap, multi-fire) emitting
  // the same welcome_popup_click event more than once per dialog session.
  const firedActionsRef = useRef<Set<string>>(new Set());
  const hasTrackedCloseRef = useRef(false);
  const c = copy[lang];

  useEffect(() => {
    // Suppress the welcome lightbox on the retail pricing page.
    if (location.pathname.includes("/retail")) return;
    try {
      const today = new Date().toISOString().slice(0, 10);
      const last = localStorage.getItem(STORAGE_KEY);
      if (last !== today) {
        const t = setTimeout(() => setOpen(true), 1200);
        return () => clearTimeout(t);
      }
    } catch {
      // ignore storage errors
    }
  }, [location.pathname]);

  const trackClick = (action: string) => {
    if (firedActionsRef.current.has(action)) {
      trackEvent("welcome_popup_duplicate_suppressed", {
        action,
        lang,
        category: "welcome_popup",
      });
      return;
    }
    firedActionsRef.current.add(action);
    trackEvent("welcome_popup_click", {
      action,
      lang,
      category: "welcome_popup",
    });
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      if (hasTrackedCloseRef.current) {
        setOpen(false);
        return;
      }
      hasTrackedCloseRef.current = true;
      // If no explicit action was set (button click), this close came from
      // the built-in X, ESC key, or overlay click — track it as close_x.
      const action = pendingCloseAction ?? "close_x";
      trackClick(action);
      setPendingCloseAction(null);
      try {
        localStorage.setItem(STORAGE_KEY, new Date().toISOString().slice(0, 10));
      } catch {
        // ignore
      }
    }
    setOpen(next);
  };

  const closeAnd = (action: string) => () => {
    setPendingCloseAction(action);
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl p-0 overflow-hidden">
        <div className="bg-gradient-to-br from-primary/10 via-background to-accent/10 p-6 sm:p-8">
          <DialogHeader className="text-center sm:text-center items-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <Sparkles className="w-6 h-6 text-primary" strokeWidth={1.75} />
            </div>
            <DialogTitle className="text-2xl font-bold text-foreground">
              {c.title}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {c.subtitle}
            </DialogDescription>
          </DialogHeader>

          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            {/* New seller */}
            <div className="rounded-xl border border-border bg-card p-5 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <UserPlus className="w-5 h-5 text-primary" strokeWidth={1.75} />
                <h3 className="font-bold text-foreground">{c.newTitle}</h3>
              </div>
              <p className="text-sm text-muted-foreground flex-1 mb-4 leading-relaxed">
                {c.newDesc}
              </p>
              <div className="flex flex-col gap-2">
                <Button asChild size="sm" className="rounded-full font-semibold">
                  <Link to={localePath("/sell")} onClick={closeAnd("new_seller_explore_program")}>
                    {c.newCta}
                  </Link>
                </Button>
                <Button asChild size="sm" variant="outline" className="rounded-full font-semibold">
                  <Link
                    to={localePath("/sell/register")}
                    onClick={closeAnd("new_seller_register")}
                  >
                    {c.newAlt}
                  </Link>
                </Button>
              </div>
            </div>

            {/* Existing seller */}
            <div className="rounded-xl border border-border bg-card p-5 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <Store className="w-5 h-5 text-primary" strokeWidth={1.75} />
                <h3 className="font-bold text-foreground">{c.oldTitle}</h3>
              </div>
              <p className="text-sm text-muted-foreground flex-1 mb-4 leading-relaxed">
                {c.oldDesc}
              </p>
              <div className="flex flex-col gap-2">
                <Button asChild size="sm" className="rounded-full font-semibold">
                  <Link to={localePath("/login")} onClick={closeAnd("existing_seller_login")}>
                    <LogIn className="w-4 h-4" /> {c.oldCta}
                  </Link>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full font-semibold"
                  onClick={closeAnd("existing_seller_new_program")}
                >
                  {c.oldAlt}
                </Button>
              </div>
            </div>
          </div>

          <div className="text-center mt-5">
            <button
              onClick={closeAnd("dismiss")}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {c.dismiss}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeDialog;
